<template>
  <v-navigation-drawer
    :value="value"
    @input="onDrawerInput"
    fixed
    right
    temporary
    width="680"
    class="artwork-text-drawer"
  >
    <v-toolbar flat color="grey lighten-3">
      <v-toolbar-title>{{ isNew ? 'Add artwork text' : 'Edit artwork text' }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="requestClose"><v-icon>close</v-icon></v-btn>
    </v-toolbar>

    <v-progress-linear v-if="loading" indeterminate color="black"></v-progress-linear>

    <div v-if="artwork" class="artwork-text-drawer__content pa-4">
      <div class="d-flex align-center mb-4">
        <v-avatar tile size="72" class="mr-3">
          <img :src="thumbnailUrl" @error="useFallbackImage" />
        </v-avatar>
        <div>
          <div class="title">{{ artwork.artist ? artwork.artist.name : artwork.name || artwork.artist_name }}</div>
          <div class="subheading" v-html="artwork.title"></div>
          <div class="caption grey--text">{{ artwork.accession_number }}</div>
        </div>
      </div>

      <v-alert v-if="error" :value="true" type="error" class="mb-3">{{ error }}</v-alert>
      <v-alert v-if="publicError" :value="true" type="warning" class="mb-3">
        {{ publicError }}
        <v-btn small flat @click="savePublicStatus" :loading="savingPublic">Retry public status</v-btn>
      </v-alert>

      <section class="drawer-section drawer-section--first">
        <div class="drawer-section__eyebrow">Public catalogue metadata</div>
        <v-text-field
          v-model="form.title"
          label="Title"
          :disabled="loading || saving || !canEdit"
        ></v-text-field>

        <v-layout row wrap mx-n2>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model="form.authors"
              label="Author(s)"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model="form.editor"
              label="Editor"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model="form.year"
              label="Year"
              type="number"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model="form.place"
              label="Place"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model="form.publisher"
              label="Publisher"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 px-2>
            <v-text-field
              v-model="form.url"
              label="External URL"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>
          </v-flex>
        </v-layout>
      </section>

      <section class="drawer-section">
        <div class="drawer-section__eyebrow">Text content</div>
        <v-tabs v-model="languageTab" color="transparent" slider-color="black" grow>
          <v-tab :ripple="false">English</v-tab>
          <v-tab :ripple="false">Español</v-tab>
          <v-tab-item>
            <div class="language-panel">
              <text-editor
                v-if="!loading"
                :value="form.content"
                :text="form"
                :artwork="artwork"
                @input="form.content = $event"
              ></text-editor>
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="language-panel">
              <text-editor
                v-if="!loading"
                :value="form.content_es"
                :text="form"
                :artwork="artwork"
                @input="form.content_es = $event"
              ></text-editor>
              <div class="caption grey--text mt-2">
                Spanish content is optional. Leave it empty when no translation is available.
              </div>
            </div>
          </v-tab-item>
        </v-tabs>
      </section>

      <section class="drawer-section drawer-section--public">
        <v-switch
          v-model="form.useOnPublicPage"
          label="Use on public page"
          color="green"
          :disabled="loading || saving || !canEdit"
          class="mt-0"
        ></v-switch>
        <div class="caption grey--text">
          This setting is artwork-specific. New texts are not public unless selected here.
        </div>
      </section>

      <section class="drawer-section drawer-section--additional">
        <v-btn flat small class="additional-toggle px-0" @click="showAdditionalMetadata = !showAdditionalMetadata">
          <v-icon small class="mr-1">{{ showAdditionalMetadata ? 'expand_less' : 'expand_more' }}</v-icon>
          Additional metadata
        </v-btn>

        <v-slide-y-transition>
          <div v-if="showAdditionalMetadata" class="additional-metadata">
            <v-text-field
              v-model="form.given_filename"
              label="Source filename"
              :disabled="loading || saving || !canEdit"
            ></v-text-field>

            <div class="caption grey--text text-uppercase mb-1">Format</div>
            <v-btn-toggle v-model="form.text_type" class="mb-3" :disabled="loading || saving || !canEdit">
              <v-btn small flat value="print">Print</v-btn>
              <v-btn small flat value="online">Online</v-btn>
            </v-btn-toggle>

            <div class="caption grey--text text-uppercase mb-1">Ownership</div>
            <v-btn-toggle v-model="form.tba" class="mb-3" :disabled="loading || saving || !canEdit">
              <v-btn small flat value="tba">TBA</v-btn>
              <v-btn small flat value="nontba">Non-TBA</v-btn>
            </v-btn-toggle>

            <v-layout row wrap mx-n2>
              <v-flex xs12 sm6 px-2>
                <v-checkbox v-model="form.publication" label="Publication" :disabled="loading || saving || !canEdit"></v-checkbox>
                <v-checkbox v-model="form.article" label="Article" :disabled="loading || saving || !canEdit"></v-checkbox>
                <v-checkbox v-model="form.artwork_text" label="Text on artwork" :disabled="loading || saving || !canEdit"></v-checkbox>
                <v-checkbox v-model="form.work_text" label="Work-inherent text" :disabled="loading || saving || !canEdit"></v-checkbox>
              </v-flex>
              <v-flex xs12 sm6 px-2>
                <v-checkbox v-model="form.pr" label="Press release" :disabled="loading || saving || !canEdit"></v-checkbox>
                <v-checkbox v-model="form.artist_documents" label="Artist documents" :disabled="loading || saving || !canEdit"></v-checkbox>
                <v-checkbox v-model="form.transcript" label="Transcript" :disabled="loading || saving || !canEdit"></v-checkbox>
                <v-checkbox v-model="form.donation" label="Donation" :disabled="loading || saving || !canEdit"></v-checkbox>
              </v-flex>
            </v-layout>

            <v-textarea
              v-model="form.notes"
              label="Notes"
              auto-grow
              rows="2"
              :disabled="loading || saving || !canEdit"
            ></v-textarea>
          </div>
        </v-slide-y-transition>
      </section>

      <div class="d-flex justify-end">
        <v-btn flat @click="requestClose">Cancel</v-btn>
        <v-btn
          dark
          color="black"
          :loading="saving"
          :disabled="loading || saving || !canEdit || !form.title"
          @click="save"
        >Save text</v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import axios from '@/utils/apiClient'
import TextEditor from './TextEditor'
import { artworkImageUrl } from '@/utils/artworkMedia'

const editableTextFields = [
  'title',
  'content',
  'content_es',
  'given_filename',
  'authors',
  'editor',
  'year',
  'place',
  'publisher',
  'url',
  'text_type',
  'tba',
  'article',
  'artwork_text',
  'pr',
  'artist_documents',
  'transcript',
  'work_text',
  'publication',
  'donation',
  'notes',
]

const emptyTextForm = () => ({
  id: null,
  title: '',
  content: '',
  content_es: '',
  given_filename: '',
  authors: '',
  editor: '',
  year: null,
  place: '',
  publisher: '',
  url: '',
  text_type: '',
  tba: '',
  article: false,
  artwork_text: false,
  pr: false,
  artist_documents: false,
  transcript: false,
  work_text: false,
  publication: false,
  donation: false,
  notes: '',
  useOnPublicPage: false,
})

export default {
  components: { TextEditor },
  props: {
    value: Boolean,
    artwork: Object,
    textSummary: Object,
  },
  data: () => ({
    form: emptyTextForm(),
    loading: false,
    saving: false,
    savingPublic: false,
    error: '',
    publicError: '',
    snapshot: '',
    loadScheduled: false,
    languageTab: 0,
    showAdditionalMetadata: false,
  }),
  computed: {
    isNew() {
      return !this.textSummary || !this.textSummary.id
    },
    canEdit() {
      return !!(this.c_user && this.c_user.edit)
    },
    isDirty() {
      return this.snapshot && this.snapshot !== this.serializeForm()
    },
    thumbnailUrl() {
      if (!this.artwork || !this.artwork.top_photo_id) return '/static/no_image.jpg'
      return artworkImageUrl(
        this.artwork,
        ['cms_big', 'cms_middle', 'cms_thumbnail'],
        this.$globals.apiserver,
        this.$globals.fileserver
      )
    },
  },
  watch: {
    value(open) {
      if (open) this.scheduleLoad()
    },
    textSummary(next, previous) {
      const nextId = next && next.id
      const previousId = previous && previous.id
      if (this.value && nextId !== previousId) this.scheduleLoad()
    },
    artwork(next, previous) {
      const nextId = next && next.id
      const previousId = previous && previous.id
      if (this.value && nextId !== previousId) this.scheduleLoad()
    },
  },
  methods: {
    scheduleLoad() {
      if (this.loadScheduled) return
      this.loadScheduled = true
      this.$nextTick(() => {
        this.loadScheduled = false
        if (this.value) this.load()
      })
    },
    serializeForm() {
      const snapshot = { id: this.form.id, useOnPublicPage: !!this.form.useOnPublicPage }
      editableTextFields.forEach(field => {
        snapshot[field] = this.form[field]
      })
      return JSON.stringify(snapshot)
    },
    async load() {
      this.error = ''
      this.publicError = ''
      this.snapshot = ''
      if (this.isNew) {
        this.form = emptyTextForm()
        this.languageTab = 0
        this.$nextTick(() => { this.snapshot = this.serializeForm() })
        return
      }

      this.loading = true
      try {
        const response = await axios.get(`${this.$globals.apiserver}/artwork_texts/${this.textSummary.id}.json`)
        this.form = {
          ...emptyTextForm(),
          ...response.data,
          useOnPublicPage: this.artwork.representative_text_id === response.data.id,
        }
        this.languageTab = 0
        this.$nextTick(() => { this.snapshot = this.serializeForm() })
      } catch (error) {
        this.error = this.errorMessage(error, 'Text could not be loaded.')
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      this.error = ''
      this.publicError = ''
      try {
        let response
        const artworkText = {
          category: 'artwork_text',
          artwork_id: this.isNew ? this.artwork.id : this.form.artwork_id,
        }
        editableTextFields.forEach(field => {
          artworkText[field] = this.form[field]
        })
        const payload = {
          artwork_text: artworkText,
        }
        if (this.isNew) {
          response = await axios.post(`${this.$globals.apiserver}/artwork_texts.json`, payload, {
            params: { return_created: true },
          })
        } else {
          response = await axios.put(`${this.$globals.apiserver}/artwork_texts/${this.form.id}.json`, payload)
        }

        this.form = { ...this.form, ...response.data }
        const summary = {
          id: response.data.id,
          title: response.data.title,
          given_filename: response.data.given_filename,
          updated_at: response.data.updated_at,
          has_english_content: this.hasTextContent(response.data.content),
          has_spanish_content: this.hasTextContent(response.data.content_es),
        }
        this.$emit('text-saved', { artworkId: this.artwork.id, summary })
        await this.savePublicStatus()
        this.snapshot = this.serializeForm()
        if (!this.publicError) this.$emit('input', false)
      } catch (error) {
        this.error = this.errorMessage(error, 'Text could not be saved.')
      } finally {
        this.saving = false
      }
    },
    async savePublicStatus() {
      if (!this.form.id) return
      const isCurrent = this.artwork.representative_text_id === this.form.id
      if (this.form.useOnPublicPage === isCurrent) return

      this.savingPublic = true
      this.publicError = ''
      const representativeTextId = this.form.useOnPublicPage ? this.form.id : null
      try {
        await axios.patch(`${this.$globals.apiserver}/artworks/${this.artwork.id}/set_representative_text`, {
          text_id: representativeTextId,
        })
        this.$emit('representative-saved', {
          artworkId: this.artwork.id,
          representativeTextId,
        })
      } catch (error) {
        this.publicError = this.errorMessage(error, 'The text was saved, but its public status was not updated.')
      } finally {
        this.savingPublic = false
      }
    },
    onDrawerInput(open) {
      if (!open) this.requestClose()
    },
    requestClose() {
      if (this.isDirty && !window.confirm('Discard unsaved text changes?')) return
      this.$emit('input', false)
    },
    useFallbackImage(event) {
      event.target.src = '/static/no_image.jpg'
    },
    errorMessage(error, fallback) {
      const data = error && error.response && error.response.data
      return (data && (data.error || (data.errors && data.errors.join(', ')))) || fallback
    },
    hasTextContent(value) {
      return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&(?:nbsp|#160);/gi, ' ')
        .trim()
        .length > 0
    },
  },
}
</script>

<style scoped>
.artwork-text-drawer { z-index: 20; }
.artwork-text-drawer__content {
  height: calc(100vh - 56px);
  overflow-y: auto;
}
.drawer-section {
  border-top: 1px solid #e3e3e3;
  margin-top: 20px;
  padding-top: 16px;
}
.drawer-section--first {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}
.drawer-section--public {
  padding-bottom: 2px;
}
.drawer-section__eyebrow {
  color: #777;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  margin-bottom: 8px;
  text-transform: uppercase;
}
.language-panel {
  padding-top: 12px;
}
.additional-toggle {
  color: #333 !important;
  font-weight: 600;
  text-transform: none;
}
.additional-metadata {
  padding-top: 8px;
}
.additional-metadata .v-input {
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/artworks/ArtworkTextDrawer.vue